'use client';
import React, { useState, useRef } from 'react';

type Status = 'idle' | 'processing' | 'done' | 'error';

export default function ExtractTextOCRTool() {
  const [status, setStatus] = useState<Status>('idle');
  const [progress, setProgress] = useState(0);
  const [progressMsg, setProgressMsg] = useState('');
  const [extractedText, setExtractedText] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processImage = async (file: File) => {
    if (!file.type.match(/^image\/(jpeg|png|webp)$/)) { alert('Please upload a JPG, PNG, or WebP image.'); return; }
    setImageUrl(URL.createObjectURL(file));
    setStatus('processing');
    setProgress(0);
    setExtractedText('');

    try {
      const Tesseract = await import('tesseract.js');
      const result = await Tesseract.recognize(file, 'eng', {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            setProgress(Math.round(m.progress * 100));
            setProgressMsg('Recognizing text…');
          } else {
            setProgressMsg(m.status);
          }
        },
      });
      setExtractedText(result.data.text.trim());
      setStatus('done');
    } catch (err) {
      console.error(err);
      setStatus('error');
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(extractedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([extractedText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'extracted_text.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    setStatus('idle');
    setExtractedText('');
    setImageUrl(null);
    setProgress(0);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-5">
      {status === 'idle' ? (
        <div
          className={`upload-zone rounded-2xl p-10 text-center cursor-pointer ${isDragging ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files?.[0]; if (f) processImage(f); }}
        >
          <div className="w-14 h-14 rounded-2xl bg-purple-50 flex items-center justify-center mx-auto mb-4">
            <span className="text-3xl">🔍</span>
          </div>
          <p className="font-semibold text-text-primary mb-1">Upload Image</p>
          <p className="text-text-muted text-sm mb-2">Drag & drop or click to browse</p>
          <p className="text-text-muted text-xs">JPG, PNG, WebP — best with clear text</p>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) processImage(f); }} />
        </div>
      ) : (
        <div className="space-y-4">
          {imageUrl && (
            <div className="flex items-start gap-4">
              <div className="w-24 h-20 rounded-xl overflow-hidden border border-gray-100 flex-shrink-0">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={imageUrl} alt="Uploaded" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1">
                {status === 'processing' && (
                  <div>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-text-secondary font-medium capitalize">{progressMsg || 'Processing…'}</span>
                      <span className="text-brand-blue font-bold">{progress}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="progress-bar h-full rounded-full" style={{ width: `${progress}%` }} />
                    </div>
                    <p className="text-text-muted text-xs mt-2">This may take 10–30 seconds on first run</p>
                  </div>
                )}
                {status === 'error' && (
                  <div className="text-red-600">
                    <p className="font-semibold">OCR failed</p>
                    <p className="text-sm mt-1">Please try with a clearer image.</p>
                    <button onClick={handleReset} className="btn-secondary text-sm px-4 py-2 mt-2">Try Again</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {status === 'done' && (
            <>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="font-semibold text-text-primary text-sm">Extracted Text</label>
                  <span className="text-text-muted text-xs">{extractedText.split(/\s+/).filter(Boolean).length} words</span>
                </div>
                <textarea
                  value={extractedText}
                  onChange={(e) => setExtractedText(e.target.value)}
                  rows={10}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-text-primary text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue/30 focus:border-brand-blue resize-y font-mono"
                />
              </div>

              <div className="flex gap-3">
                <button onClick={handleCopy} className="btn-secondary flex-1 py-3 flex items-center justify-center gap-2 text-sm">
                  {copied ? (
                    <><svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>Copied!</>
                  ) : (
                    <><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" /></svg>Copy Text</>
                  )}
                </button>
                <button onClick={handleDownload} className="btn-primary flex-1 py-3 flex items-center justify-center gap-2 text-sm">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" /></svg>
                  Download TXT
                </button>
              </div>
              <button onClick={handleReset} className="w-full text-text-muted text-sm hover:text-text-primary transition-colors py-2">Process Another Image</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
